import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { Filter, Search, Calendar, User, Clock, Flame, Globe, Tv } from 'lucide-react';
import { useArticles } from '../hooks/useArticles';
import { Link } from 'react-router-dom';
import ContentRow from '../components/ContentRow';
import StreamfinitePreview from '../components/StreamfinitePreview';

// SectionLabel component - Outrange style
const SectionLabel = ({ children, accent = '#a78bfa', icon: Icon = Globe }) => (
  <div className="flex items-center gap-3" style={{ marginBottom: '24px' }}>
    <div 
      className="w-[3px] rounded-[2px] h-[26px]" 
      style={{ background: `linear-gradient(to bottom, ${accent}, transparent)` }} 
    />
    <Icon size={12} color={accent} />
    <span 
      className="font-azonix text-[10px] uppercase tracking-[3.5px]" 
      style={{ color: accent }}
    >
      {children}
    </span>
  </div>
);

/* ─────────────────────────────────────────────
   GLOBAL STYLES + FONT INJECTION
───────────────────────────────────────────── */
const FontStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');

    @font-face {
      font-family: 'Japanese3017';
      src: url('/fonts/Japanese-3017.ttf') format('truetype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Acknowledgement';
      src: url('/fonts/Acknowledgement.otf') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    @font-face {
      font-family: 'Azonix';
      src: url('/fonts/Azonix.otf') format('opentype'),
           url('/fonts/azonix.woff2') format('woff2'),
           url('/fonts/azonix.woff') format('woff');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }

    :root {
      --accent-east: #f97316;
      --accent-east-glow: rgba(249,115,22,0.35);
      --accent-west: #3b82f6;
      --accent-west-glow: rgba(59,130,246,0.35);
      --accent-neutral: #a78bfa;
      --glass-bg: rgba(255,255,255,0.04);
      --glass-border: rgba(255,255,255,0.08);
      --font-azonix: 'Azonix', 'Impact', sans-serif;
      --font-japanese: 'Japanese3017', 'Noto Serif JP', 'Hiragino Mincho Pro', serif;
      --font-west: 'Acknowledgement', 'Azonix', 'Impact', sans-serif;
      --font-body: 'Georgia', serif;
      --font-ui: -apple-system, 'Helvetica Neue', sans-serif;
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .ep-root {
      background: #08080c;
      color: #fff;
      font-family: var(--font-ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    .ep-hero {
      position: relative;
      width: 100%;
      height: 60vh;
      min-height: 400px;
      overflow: hidden;
      display: flex;
      align-items: flex-end;
    }
    .ep-hero-bg {
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      object-position: center top;
    }
    .ep-hero-overlay-lr {
      position: absolute; inset: 0; z-index: 2;
      background: linear-gradient(
        to right,
        rgba(8,8,12,0.96) 0%,
        rgba(8,8,12,0.65) 42%,
        rgba(8,8,12,0.12) 72%,
        rgba(8,8,12,0.04) 100%
      );
    }
    .ep-hero-overlay-bt {
      position: absolute; inset: 0; z-index: 2;
      background: linear-gradient(
        to top,
        rgba(8,8,12,1) 0%,
        rgba(8,8,12,0.5) 28%,
        transparent 58%
      );
    }
    .ep-hero-body {
      position: relative; z-index: 4;
      width: 100%;
      padding: 0 clamp(20px,5vw,60px) clamp(48px,7vh,80px);
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 28px;
    }
    .ep-hero-left { max-width: 560px; flex: 1; }
    .ep-hero-badge {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 5px 14px; border-radius: 100px;
      background: rgba(249,115,22,0.12);
      border: 1px solid rgba(249,115,22,0.25);
      margin-bottom: 20px;
      font-size: 10px; color: var(--accent-east);
      letter-spacing: 2.5px; text-transform: uppercase; font-weight: 600;
      font-family: var(--font-ui);
      margin-top: 120px;
    }
    .ep-hero-title {
      font-family: var(--font-japanese);
      font-size: clamp(40px, 7vw, 96px);
      line-height: 0.93;
      letter-spacing: -1.5px;
      color: #fff;
      margin-bottom: 16px;
    }
    .ep-hero-desc {
      font-family: var(--font-ui);
      font-size: clamp(14px,1.7vw,16px);
      color: rgba(255,255,255,0.42);
      line-height: 1.85; max-width: 430px;
      margin-bottom: 26px;
    }

    .ep-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,48px);
      position: relative; z-index: 1;
    }
    .ep-section   { padding-bottom: clamp(56px,8vh,96px); }

    .ep-label {
      display:flex; align-items:center; gap:12px;
      margin-bottom: 24px;
    }
    .ep-label-bar { width:3px; border-radius:2px; height:26px; }
    .ep-label-text {
      font-family: var(--font-azonix);
      font-size: 16px; letter-spacing: 1px; text-transform:uppercase;
    }

    .ep-grid-card {
      border-radius:13px; overflow:hidden;
      background:var(--glass-bg); border:1px solid var(--glass-border);
      text-decoration:none; display:block;
      backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px);
      transition:all 0.28s ease;
    }
    .ep-grid-card:hover {
      background:rgba(255,255,255,0.07);
      border-color:rgba(255,255,255,0.14);
      transform:translateY(-4px);
      box-shadow:0 18px 56px rgba(0,0,0,0.42);
    }
    .ep-grid-card-img {
      position:relative; aspect-ratio:16/9; overflow:hidden;
    }
    .ep-grid-card-img img {
      width:100%; height:100%; object-fit:cover;
      transition:transform 0.5s ease;
    }
    .ep-grid-card:hover .ep-grid-card-img img { transform:scale(1.06); }
    .ep-grid-card-body { padding:11px 13px 13px; }
    .ep-grid-card-title {
      font-size:12px; font-weight:600; color:#fff; line-height:1.4;
      display:-webkit-box; -webkit-line-clamp:2;
      -webkit-box-orient:vertical; overflow:hidden;
      font-family:var(--font-ui);
    }

    .ep-hero-art {
      position:relative; border-radius:18px; overflow:hidden;
      height:clamp(280px,38vw,460px);
      display:block; text-decoration:none; cursor:pointer;
    }
    .ep-hero-art img {
      position:absolute; inset:0;
      width:100%; height:100%; object-fit:cover;
      transition:transform 0.7s ease;
    }
    .ep-hero-art:hover img { transform:scale(1.04); }
    .ep-hero-art-grad {
      position:absolute; inset:0;
      background:linear-gradient(to top,rgba(0,0,0,0.92) 0%,rgba(0,0,0,0.3) 50%,transparent 100%);
    }
    .ep-hero-art-body {
      position:absolute; bottom:0; left:0; right:0;
      padding: clamp(16px,2.5vw,24px);
    }
    .ep-cat-tag {
      font-family:var(--font-azonix);
      font-size:9px; font-weight:700;
      letter-spacing:2.5px; text-transform:uppercase;
      margin-bottom:8px; display:inline-block;
    }
    .ep-hero-art-title {
      font-family:var(--font-azonix);
      font-size:clamp(14px,2.5vw,22px);
      line-height:1.2; color:#fff;
      display:-webkit-box; -webkit-line-clamp:2;
      -webkit-box-orient:vertical; overflow:hidden;
      margin-bottom:10px;
    }
    .ep-art-meta {
      display:flex; gap:12px; align-items:center;
      font-size:10px; color:rgba(255,255,255,0.38);
      font-family:var(--font-ui);
    }
    .ep-art-meta span { display:flex; align-items:center; gap:4px; }
  `}</style>
);

const EastPortal = () => {
  const pageTitle = "East Portal • Anime Culture Chronicles";
  const pageDescription = "Dive deep into anime, manga, and Japanese pop culture with seasonal reviews, studio insights, and cultural spotlights.";
  const pageUrl = "https://animac-metaverse.vercel.app/outrange/east"; // Replace with your actual site URL
  const previewImage = "https://animac-metaverse.vercel.app/assets/Outrange east.png"; // Replace with your actual image URL

  const { articles: eastArticles, loading } = useArticles('east');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  // Get unique tags from articles
  const allTags = [...new Set(eastArticles.flatMap(article => article.tags || []))];

  // Filter articles based on search and tag
  const filteredArticles = eastArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = !selectedTag || (article.tags && article.tags.includes(selectedTag));
    return matchesSearch && matchesTag;
  });

  // Featured article (first featured or first article)
  const featuredArticle = eastArticles.find(article => article.is_featured) || (eastArticles.length > 0 ? eastArticles[0] : null);

  return (

    <>
      <FontStyles />
      <SEO
        title={pageTitle}
        description={pageDescription}
        url={pageUrl}
        image={previewImage}
        type="website"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="ep-root"
      >
        {/* East Portal Header */}
        <div className="ep-hero">
          <div className="ep-hero-bg" style={{ backgroundImage: "url('https://i.pinimg.com/1200x/2d/50/93/2d509399aceab6e117c84583a5b1679f.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }} />

          <div className="ep-hero-overlay-lr" />
          <div className="ep-hero-overlay-bt" />

          <div className="ep-hero-body">
            <div className="ep-hero-left">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
              >
                <div className="ep-hero-badge">
                  <Flame size={10} />
                  OUTRANGE • EAST PORTAL
                </div>

                <h1 className="ep-hero-title">
                  EAST
                </h1>

                <p className="ep-hero-desc">
                  Immerse yourself in the world of anime, manga, and Japanese pop culture.
                  From seasonal breakdowns to studio deep-dives, discover the stories that shape anime culture.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="ep-container ep-section">
          {/* ── STREAMFINITE AUTOPLAY (prominent, with debug) ── */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{ width: 3, height: 20, background: 'linear-gradient(to bottom, #f97316, transparent)', borderRadius: 2 }} />
              <Tv size={13} color="#f97316" />
              <span style={{ fontFamily: 'var(--font-azonix)', fontSize: 11, letterSpacing: 2, color: '#f97316', textTransform: 'uppercase' }}>Streamfinite • East Trailers (debug active)</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 16, overflow: 'hidden' }}>
              <StreamfinitePreview filter="anime" /> {/* Anime-only themed preview using project's Jikan API */}
            </div>
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <Link to="/streamfinite" style={{ fontSize: 10, color: '#f97316', textDecoration: 'none', fontFamily: 'var(--font-azonix)', letterSpacing: 1 }}>Enter Streamfinite →</Link>
            </div>
          </div>

          {/* Search and Filter Section */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="flex flex-col md:flex-row gap-4 mb-12"
          >
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search anime articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-netflix-dark border border-gray-700 rounded-lg text-white font-inter placeholder-gray-400 focus:outline-none focus:border-east-500 transition-colors"
              />
            </div>

            {/* Tag Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="pl-10 pr-8 py-3 bg-netflix-dark border border-gray-700 rounded-lg text-white font-inter focus:outline-none focus:border-east-500 transition-colors appearance-none"
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </motion.div>

          {/* Featured Article */}
          <AnimatePresence mode="wait">
            {featuredArticle && (
              <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mb-16"
            >
              <SectionLabel accent="#f97316" icon={Flame}>
                Featured Story
              </SectionLabel>

              <Link to={`/article/${featuredArticle.slug}`}>
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-east-900/40 to-netflix-dark hover-glow-east transition-all duration-300 hover:scale-[1.02]">
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent z-10" />
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{
                      backgroundImage: featuredArticle?.featured_image
                        ? `url('${featuredArticle.featured_image}')`
                        : `url('https://images.unsplash.com/photo-1615592389070-bcc97e05ad01')`
                    }}
                  />

                  <div className="relative z-20 p-8 md:p-12">
                    <div className="max-w-2xl">
                      <div className="inline-block px-3 py-1 bg-east-500/20 border border-east-500/30 rounded-full text-east-300 font-inter font-semibold text-sm mb-4">
                        FEATURED • ANIME
                      </div>

                      <h3 className="font-montserrat text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                        {featuredArticle.title}
                      </h3>

                      <p className="text-lg text-gray-300 font-inter leading-relaxed mb-6">
                        {featuredArticle.excerpt}
                      </p>

                      <div className="flex items-center space-x-6 text-sm text-gray-400">
                        <span className="flex items-center space-x-2">
                          <User size={16} />
                          <span>{featuredArticle.author}</span>
                        </span>
                        <span className="flex items-center space-x-2">
                          <Clock size={16} />
                          <span>{featuredArticle.read_time} min read</span>
                        </span>
                        <span className="flex items-center space-x-2">
                          <Calendar size={16} />
                          <span>{new Date(featuredArticle.created_at).toLocaleDateString()}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Articles Grid */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8">
              <SectionLabel accent="#f97316" icon={Flame}>
                Latest Articles
              </SectionLabel>
              <div className="text-sm text-gray-400 font-inter">
                {filteredArticles.length} articles found
              </div>
            </div>

            {/* Content Row - Trending Now Carousel */}
            <ContentRow
              title="Trending Now"
              articles={filteredArticles.slice(0, 8)}
              category="east"
              emptyMessage="No trending articles at the moment."
            />

            {/* Additional Carousel Section - Seasonal Picks */}
            <ContentRow
              title="Seasonal Picks"
              articles={[...filteredArticles].reverse().slice(0, 6)}
              category="east"
              emptyMessage="No seasonal articles available."
            />

            {/* Additional Carousel Section - Must Read */}
            <ContentRow
              title="Must Read"
              articles={filteredArticles.filter(a => a.is_featured).slice(0, 6)}
              category="east"
              emptyMessage="No featured articles at the moment."
            />

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-800 rounded-lg h-64 mb-4"></div>
                    <div className="bg-gray-700 h-4 rounded mb-2"></div>
                    <div className="bg-gray-700 h-3 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article, index) => (
                  <motion.div
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                  >
                    <Link to={`/article/${article.slug}`}>
                      <div className="netflix-card bg-netflix-dark rounded-lg overflow-hidden border border-east-500/20 hover-glow-east hover:border-east-500/60 transition-all duration-300">
                        {/* Article Image */}
                        <div className="relative h-48 bg-gradient-to-br from-east-800/30 to-gray-900 overflow-hidden">
                          {article.featured_image ? (
                            <img
                              src={article.featured_image}
                              alt={article.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-east-500/30 text-2xl font-azonix">
                                EAST
                              </div>
                            </div>
                          )}

                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                          {article.is_featured && (
                            <div className="absolute top-3 right-3 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs border border-yellow-500/30">
                              Featured
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <h3 className="font-montserrat font-semibold text-lg mb-3 text-white line-clamp-2 leading-tight">
                            {article.title}
                          </h3>

                          <p className="text-gray-400 text-sm mb-4 line-clamp-3 font-inter leading-relaxed">
                            {article.excerpt}
                          </p>

                          {/* Article Meta */}
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                            <span className="flex items-center space-x-1">
                              <User size={12} />
                              <span>{article.author}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <Clock size={12} />
                              <span>{article.read_time} min</span>
                            </span>
                          </div>

                          {/* Tags */}
                          {article.tags && article.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {article.tags.slice(0, 3).map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="text-xs px-2 py-1 bg-east-500/20 text-east-300 rounded border border-east-500/30 hover:bg-east-500/30 transition-colors cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedTag(tag);
                                  }}
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}

            {!loading && filteredArticles.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">😔</div>
                <h3 className="font-montserrat text-xl font-semibold text-white mb-2">
                  No articles found
                </h3>
                <p className="text-gray-400 font-inter">
                  Try adjusting your search terms or filters
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </>
  );    
};

      export default EastPortal;